import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, ChevronDown, CheckCircle2, ListVideo } from "lucide-react";
import { api, mediaUrl, formatQuarter } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Overline, Rule } from "@/components/site/Primitives";
import { VideoPlayer } from "@/components/site/VideoPlayer";
import { VideoCard } from "@/components/site/VideoCard";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ConsultCTA } from "@/components/site/ConsultCTA";

export default function VideoDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/public/videos/${slug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-32 text-center">
        <h1 className="font-serif text-4xl font-light text-ink">Video not found</h1>
        <Link to="/videos" className="mt-8 inline-flex font-sans text-gold-600 gf-link">Back to the video library →</Link>
      </div>
    );
  }
  if (!data) return <div className="py-40 text-center font-sans text-steel-500">Loading…</div>;

  const v = data.video;

  return (
    <>
      <Seo
        title={v.seo_title || v.title}
        description={v.seo_description || v.description}
        image={mediaUrl(v.thumbnail)}
        type="video.other"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: v.title,
          description: v.description,
          thumbnailUrl: v.thumbnail ? [mediaUrl(v.thumbnail)] : undefined,
          uploadDate: v.published_at,
        }}
      />

      <div className="bg-ink pt-10">
        <div className="mx-auto max-w-5xl px-5">
          <VideoPlayer video={v} className="shadow-[0_20px_60px_rgba(0,0,0,0.4)]" />
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-5 py-14">
        {v.topic && <Link to={`/videos?topic=${encodeURIComponent(v.topic)}`}><Overline>{v.topic}</Overline></Link>}
        <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-ink">{v.title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-sans text-sm text-steel-500">
          <span>{formatQuarter(v.published_at)}</span>
          {v.duration && <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {v.duration}</span>}
          {v.provider !== "file" && <span className="text-steel-400">A third-party player loads only when you press play.</span>}
        </div>
        <Rule className="mt-6" />

        {v.description && <p className="mt-8 font-sans text-lg leading-relaxed text-steel-700">{v.description}</p>}

        {v.summary && (
          <div className="mt-10">
            <h2 className="font-serif text-2xl font-light text-ink">Summary</h2>
            <p className="mt-3 font-sans leading-relaxed text-steel-600">{v.summary}</p>
          </div>
        )}

        {v.takeaways?.length > 0 && (
          <div className="mt-10">
            <h2 className="font-serif text-2xl font-light text-ink">Key takeaways</h2>
            <ul className="mt-4 space-y-3">
              {v.takeaways.map((t, i) => (
                <li key={i} className="flex items-start gap-3 font-sans text-steel-700">
                  <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-gold-500" /> {t}
                </li>
              ))}
            </ul>
          </div>
        )}

        {v.chapters?.length > 0 && (
          <div className="mt-10">
            <h2 className="flex items-center gap-2 font-serif text-2xl font-light text-ink"><ListVideo className="h-5 w-5 text-gold-500" /> Chapters</h2>
            <ul className="mt-4 divide-y divide-cream-200 border-y border-cream-200">
              {v.chapters.map((c, i) => (
                <li key={i} className="flex items-center gap-4 py-3 font-sans text-sm">
                  <span className="w-14 text-gold-600">{c.time}</span>
                  <span className="text-steel-700">{c.title}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {v.transcript && (
          <div className="mt-10 border border-cream-200">
            <button onClick={() => setShowTranscript((s) => !s)} className="flex w-full items-center justify-between px-6 py-4 font-serif text-2xl font-light text-ink" data-testid="transcript-toggle">
              Transcript
              <ChevronDown className={`h-5 w-5 transition-transform ${showTranscript ? "rotate-180" : ""}`} />
            </button>
            {showTranscript && (
              <div className="border-t border-cream-200 px-6 py-5 font-sans text-sm leading-relaxed text-steel-600 whitespace-pre-line">{v.transcript}</div>
            )}
          </div>
        )}
      </article>

      {(data.related_articles?.length > 0 || data.related_videos?.length > 0) && (
        <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10">
          {data.related_articles?.length > 0 && (
            <div className="mb-16">
              <Overline>Supporting Articles</Overline>
              <h2 className="mb-10 mt-3 font-serif text-3xl font-light text-ink">Read more on this topic</h2>
              <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
                {data.related_articles.map((a) => <ArticleCard key={a.id} article={a} />)}
              </div>
            </div>
          )}
          {data.related_videos?.length > 0 && (
            <div>
              <Overline>Related Videos</Overline>
              <h2 className="mb-10 mt-3 font-serif text-3xl font-light text-ink">More from the library</h2>
              <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
                {data.related_videos.map((rv) => <VideoCard key={rv.id} video={rv} />)}
              </div>
            </div>
          )}
        </section>
      )}

      <ConsultCTA compact />
    </>
  );
}
